import clsx from "clsx";
import { useState, type ReactNode } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { GUILDS } from "../data/seed";
import { usePortal } from "../state/PortalContext";
import type { UserProfile } from "../types/domain";
import { Button, ProgressBar } from "./ui";
import { Icon } from "./Icon";
import { Logo } from "./Logo";

interface NavigationLink {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

const studentLinks: NavigationLink[] = [
  { to: "/app/map", label: "World Map", icon: "Map" },
  { to: "/app/dashboard", label: "Adventure Hub", icon: "LayoutDashboard" },
  { to: "/app/profile", label: "My Character", icon: "User" },
];

const facultyLinks: NavigationLink[] = [
  { to: "/faculty", label: "Command Center", icon: "LayoutDashboard", end: true },
  { to: "/faculty/curriculum", label: "Curriculum Forge", icon: "Edit3" },
  { to: "/faculty/cohorts", label: "Cohorts & Rosters", icon: "Users" },
  { to: "/faculty/analytics", label: "Learning Signals", icon: "BarChart3" },
];

export function AppShell({ faculty = false }: { faculty?: boolean }) {
  const { user, signOut, isDemo } = usePortal();
  const [mobileOpen, setMobileOpen] = useState(false);
  const links = faculty ? facultyLinks : studentLinks;
  const guild = GUILDS.find((entry) => entry.id === user?.guild);

  const navigation = (
    <>
      <div className="flex items-center justify-between">
        <Logo />
        <button
          className="rounded-xl p-2 text-slate-400 hover:bg-slate-800 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation"
        >
          <Icon name="PanelLeftClose" size={20} />
        </button>
      </div>
      <nav className="mt-9 space-y-2" aria-label={faculty ? "Faculty" : "Student"}>
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end ?? false}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              clsx(
                "flex min-h-12 items-center gap-3 rounded-2xl px-4 text-sm font-bold transition",
                isActive
                  ? "bg-indigo-500/15 text-white ring-1 ring-indigo-400/30"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-white",
              )
            }
          >
            <Icon name={link.icon} size={19} />
            {link.label}
          </NavLink>
        ))}
        {user?.role !== "student" && (
          <NavLink
            to={faculty ? "/app/map" : "/faculty"}
            className="mt-5 flex min-h-12 items-center gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/5 px-4 text-sm font-bold text-amber-200 transition hover:bg-amber-400/10"
          >
            <Icon name={faculty ? "Gamepad2" : "WandSparkles"} size={19} />
            {faculty ? "Student Preview" : "Faculty Tools"}
          </NavLink>
        )}
      </nav>
      <div className="mt-auto space-y-4 pt-8">
        {isDemo && (
          <div className="rounded-xl border border-cyan-400/15 bg-cyan-400/5 px-3 py-2 text-[10px] font-bold text-cyan-200">
            DEMO DATA MODE
          </div>
        )}
        {!faculty && user && (
          <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="font-bold text-slate-300">Level {user.level}</span>
              <span className="text-slate-500">
                {user.xp}/{user.xpNext} XP
              </span>
            </div>
            <ProgressBar value={(user.xp / user.xpNext) * 100} />
            <p className="mt-3 text-[10px] font-bold" style={{ color: guild?.color }}>
              {guild?.name}
            </p>
          </div>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={() => void signOut()}
        >
          <Icon name="LogOut" size={18} />
          Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-slate-800/80 bg-game-ink/95 p-6 backdrop-blur-xl lg:flex">
        {navigation}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-black/70"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation overlay"
          />
          <aside className="absolute inset-y-0 left-0 flex w-[min(86vw,320px)] flex-col border-r border-slate-800 bg-game-ink p-6">
            {navigation}
          </aside>
        </div>
      )}

      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-game-ink/80 px-4 py-3 backdrop-blur-xl sm:px-7">
          <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4">
            <button
              className="rounded-xl border border-slate-800 bg-slate-900 p-2.5 text-slate-300 lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Icon name="Menu" size={20} />
            </button>
            <div className="hidden lg:block">
              <p className="font-retro text-[8px] tracking-[0.2em] text-slate-500">
                {faculty ? "FACULTY OPERATIONS" : "ADVENTURE SESSION"}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <div className="hidden text-right sm:block">
                <p className="text-sm font-bold text-white">{user?.fullName}</p>
                <p className="text-[11px] capitalize text-slate-500">{user?.role}</p>
              </div>
              <div className="grid h-11 w-11 place-items-center overflow-hidden rounded-2xl border-2 border-indigo-400/50 bg-gradient-to-br from-indigo-500 to-violet-700 text-white">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={21} />
                )}
              </div>
            </div>
          </div>
        </header>
        <main className="mx-auto min-h-[calc(100vh-69px)] max-w-[1500px] p-4 sm:p-7 lg:p-9">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function ProtectedRoute({
  roles,
  children,
}: {
  roles?: UserProfile["role"][];
  children: ReactNode;
}) {
  const { user, authLoading } = usePortal();
  if (authLoading) {
    return (
      <div className="grid min-h-screen place-items-center">
        <div className="text-center">
          <Icon
            name="Compass"
            size={42}
            className="mx-auto animate-spin text-indigo-300"
          />
          <p className="mt-4 text-sm text-slate-400">Charting your route...</p>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/" replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return (
      <Navigate
        to={user.role === "student" ? "/app/map" : "/faculty"}
        replace
      />
    );
  }
  return children;
}
